import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAllEvents, deleteEvent, scrapeWsdcEvents } from '../lib/api-client';
import type { Event } from '../types';

const AdminContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const AdminCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  marginBottom: theme.spacing(3),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
}));

/**
 * TODO: We want this list to mimic the events list from the wsdc page.
 * We'll want to setup a routine to scrape the events from the wsdc page, and place them here.
 * Any event that's not "approved" should be marked as "pending" and have a different style.
 * Admins should be able to edit and approve events from this page.
 * When editing/approving event, the raw data that was scrapped should be visible for reference.
 */
export const EventsAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [scraping, setScraping] = useState(false);
  const [scrapeMessage, setScrapeMessage] = useState<string | null>(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await getAllEvents();
      setEvents(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleDelete = async (eventId: string, eventName: string) => {
    if (!confirm(`Are you sure you want to delete "${eventName}"?`)) {
      return;
    }

    try {
      await deleteEvent(eventId);
      setEvents(events.filter((e) => e.id !== eventId));
      setDeleteError(null);
    } catch (err: any) {
      console.error('Failed to delete event:', err);
      setDeleteError(err.message || 'Failed to delete event');
    }
  };

  const handleEdit = (eventId: string) => {
    navigate(`/events-admin/${eventId}`);
  };

  const handleAdd = () => {
    navigate('/events-admin/new');
  };

  const handleScrape = async () => {
    try {
      setScraping(true);
      setScrapeMessage(null);
      const result = await scrapeWsdcEvents();
      setScrapeMessage(result.message);
      // Refresh events list after scraping
      await fetchEvents();
    } catch (err: any) {
      console.error('Failed to scrape events:', err);
      setError(err.message || 'Failed to scrape events');
    } finally {
      setScraping(false);
    }
  };

  if (loading) {
    return (
      <AdminContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminCard>
        <HeaderBox>
          <Typography variant="h4" component="h1">
            Events Administration
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={scraping ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleScrape}
              disabled={scraping}
            >
              {scraping ? 'Scraping...' : 'Scrape WSDC'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                },
              }}
            >
              Add Event
            </Button>
          </Box>
        </HeaderBox>

        {scrapeMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setScrapeMessage(null)}>
            {scrapeMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {deleteError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDeleteError(null)}>
            {deleteError}
          </Alert>
        )}

        {events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">No events found. Create your first event!</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {events.map((event) => (
              <Card
                key={event.id}
                variant="outlined"
                sx={{
                  border: '1px solid #e6e6e6',
                  borderRadius: '12px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {event.name}
                        </Typography>
                        <Chip
                          label={event.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={event.isActive ? 'success' : 'default'}
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        📅{' '}
                        {new Date(event.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        📍 {event.location}
                      </Typography>
                      {event.venueName && (
                        <Typography variant="body2" color="textSecondary">
                          🏛️ {event.venueName}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={() => handleEdit(event.id)}
                        color="primary"
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(event.id, event.name)}
                        color="error"
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </AdminCard>
    </AdminContainer>
  );
};
